import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useLocation } from "wouter";
import { getLoginPath } from "@/const";
import { trpc } from "@/lib/trpc";

/**
 * The route gate for every admin screen.
 *
 * Anyone who is not a signed-in admin is sent to secure sign-in and the admin
 * screen never renders. Three details are the substance of it:
 *
 *   1. The role is read from `auth.me`, which reports the persisted server role
 *      for the session cookie. A locally stored profile cannot grant admin.
 *   2. A signed-in member is redirected exactly like an anonymous visitor. The
 *      alternative — an "access denied" page — confirms to any signed-in account
 *      that an admin surface lives at this address.
 *   3. Nothing is rendered until the role is known. Showing the CRM shell first
 *      and redirecting afterwards would paint admin structure to whoever asked
 *      for it, however briefly.
 *
 * This is the courteous half of the control. Every admin procedure refuses
 * unauthorized callers on the server regardless of what the client renders —
 * see `server/adminAuthorizationSurface.test.ts`, which asserts exactly that for
 * every procedure on the router.
 */
export default function RequireAdmin({ children }: { children: React.ReactNode }) {
  const [, navigate] = useLocation();
  const session = trpc.auth.me.useQuery(undefined, { retry: false, refetchOnWindowFocus: false });

  const settled = !session.isLoading;
  const isAdmin = session.data?.role === "admin";

  useEffect(() => {
    if (!settled || isAdmin) return;
    /*
     * The whole path, so sign-in returns the admin to the screen they asked for
     * rather than the CRM landing page. getLoginPath runs it through
     * toSafeAppPath, and resolvePostLoginPath refuses to send a non-admin to an
     * admin path, so a member who lands here is returned to their own dashboard.
     */
    const requested = `${window.location.pathname}${window.location.search}`;
    navigate(getLoginPath(requested), { replace: true });
  }, [settled, isAdmin, navigate]);

  if (!settled || !isAdmin) {
    return (
      <div
        role="status"
        aria-label={settled ? "Redirecting to secure sign-in" : "Checking administrator access"}
        style={{ minHeight: "100vh", background: "#080C18", display: "grid", placeItems: "center", color: "#94A3B8" }}
      >
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return <>{children}</>;
}
